import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
// material
import { Box, Button, Divider, Radio, Modal, Typography, Stack, IconButton } from '@mui/material';
// components
import { PATH_DEALER, PATH_SalesUser, PATH_CoE, PATH_Admin, PATH_BranchSecretaryUser, PATH_COEAdmin, PATH_ReportUser, PATH_DashboardAndReportUser, PATH_MasterUser, PATH_MasterViewer } from '../../routes/paths';
import { switchUser, revertUser } from '../../sections/common/redux/actions';
import Iconify from '../iconify';
import ExtendedCommonDataGrid from '../extendedcommondatagrid/extendedcommondatagrid';
import transformColumnsForTanStack from '../extendedcommondatagrid/transformColumnsForTanStack';
import ConfirmationPopup from '../modal-popup/confirmationpopup';
import UserMasterService from '../../services/userMaster.service';
import OrgReuploadService from '../../services/orgreupload.service';
import AppCache from '../../services/appcache';
import { handleError } from '../../auth/utils';
import { DataUploadApprovePagefieldMapping, ReloadZylemDataPagefieldMapping, DataMappingPagefieldMapping, EnableReuploadPagefieldMapping, DistributorConfigurationPagefieldMapping, SystemConfigurationPagefieldMapping, MastersPagefieldMapping, ReportPagefieldMapping, DashboardPagefieldMapping, DataUploadPagefieldMapping, SNSUploadPagefieldMapping, OnlineEntryPagefieldMapping, MonthClosurePagefieldMapping, DataPagefieldMapping, HelpTopicPagefieldMapping, AddEditHelpTopicsPagefieldMapping, AdminMastersPagefieldMapping, SubCategoryPagefieldMapping, HelpAndCommunicationPagefieldMapping } from '../../sections/common/moduleAccessFieldMappings';
// ------------------------------------------------------------------------------------------------

SwitchUserModalPopup.propTypes = {
    handleCloseSwitchUserPopup: PropTypes.func,
};
// ------------------------------------------------------------------------------------------------

const buttonStyle = {
    backgroundColor: 'common.white',
    color: 'common.black',
    border: '1.5px solid',
    borderColor: 'grey.300',
    mx: 1,
    ':hover': {
        backgroundColor: 'common.white',
        boxShadow: 10,
    }
}
// ----------------------------------------------------------------------------------------------------------------

export default function SwitchUserModalPopup({ handleCloseSwitchUserPopup }) {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInUser = AppCache?.GetCurrentLoggedInUser();
    const primaryUser = AppCache?.GetPrimaryUser()?.CurrentLoggedInUser;
    const primaryUserName = `${primaryUser?.UserFirstName} ${primaryUser?.UserLastName}`;
    const isImpersonating = loggedInUser.IsImpersonating;

    const [rowData, setRowData] = useState([]);
    const [columnData, setColumnData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [removeFilter, setRemoveFilter] = useState(false);
    const [filteredData, setFilteredData] = useState(rowData);
    const [apiGetCall, setApiGetCall] = useState(false);
    const [clear, setClear] = useState(false);
    const [clickedRowId, setClickedRowId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [switchUserConfirmation, setSwitchUserConfirmation] = useState(false);
    const [switchBackToUserConfirmation, setSwitchBackToUserConfirmation] = useState(false);

    const ClearFilters = () => {
        setClear(!clear);
        setSelectedRows([]);
        setRemoveFilter(true);
        setClickedRowId(null);
    }

    useEffect(() => {
        setLoading(true);
        setColumnData(OrgReuploadService.GetColumns('switchuser'));

        UserMasterService.GetDataUserAccessList()
            .then((response) => {
                if (response.status === 200) {
                    setLoading(false);
                    setRowData(response.data);
                }
                else {
                    setLoading(false);
                    console.log("error");
                }
            })
            .catch(_error => {
                handleError(_error, navigate, "", setLoading);
            });
    }, [navigate]);

    const actionColumn = useMemo(() => ({
        id: 'actions',
        header: '',
        size: 25,
        minSize: 30,
        maxSize: 30,
        enableResizing: false,
        enableSorting: false,
        enableColumnFilter: false,
        enableRowSelection: false,
        enablePinning: true,
        textAlign: "center",
        showInGrid: true,
        cell: ({ row }) => {
            const user = row.original;
            return (
                <Radio
                    size="small"
                    checked={selectedUserId === user.userId}
                    onChange={() => setSelectedUserId(user.userId)}
                    value={user.userId}
                    name="selectedUser"
                    sx={{
                        height: '16px',
                        width: '16px',
                        padding: '10px',
                    }}
                />
            );
        }
    }), [selectedUserId]);

    const handleSwitchToUser = () => {
        setSwitchUserConfirmation(true);
    };

    const handleSwitchToUserConfirmation = (value, data) => {
        if (value === true) {
            setLoading(true);
            const payload = {
                TargetUserId: selectedUserId,
                SessionId: loggedInUser.SessionId
            }
            UserMasterService.SwitchToUser(payload)
                .then((response) => {
                    if (response.status === 200) {
                        const switchedUserData = response.data;
                        const role = switchedUserData.userRole?.toLowerCase();
                        const previousToken = AppCache.GetHeaderToken();
                        AppCache.SwitchLoggedInUser(switchedUserData);
                        // Module Access Starts
                        const dataUploadApprovePageModule = {};
                        const mastersPagefieldMappingModule = {};
                        const reloadZylemDataPageModule = {};
                        const dataMappingPageModule = {};
                        const enableReuploadPageModule = {};
                        const distributorConfigurationPageModule = {};
                        const systemConfigurationPageModule = {};
                        const reportPageModule = {};
                        const dashboardPageModule = {};
                        const dataUploadPageModule = {};
                        const snsUploadPageModule = {};
                        const onlineEntryPageModule = {};
                        const monthClosurePageModule = {};
                        const dataPageModule = {};
                        const helpTopicPageModule = {};
                        const addEditHelpTopicsPageModule = {};
                        const adminMastersPageModule = {};
                        const subCategoryPageModule = {};
                        const helpAndCommunicationPageModule = {};

                        switchedUserData?.userAccessData.forEach(item => {
                            if (item?.moduleName === "DataUploadApprovePage" && DataUploadApprovePagefieldMapping[item?.accessString]) {
                                dataUploadApprovePageModule[DataUploadApprovePagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "ReloadZylemDataPage" && ReloadZylemDataPagefieldMapping[item?.accessString]) {
                                reloadZylemDataPageModule[ReloadZylemDataPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "DataMappingPage" && DataMappingPagefieldMapping[item?.accessString]) {
                                dataMappingPageModule[DataMappingPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "EnableReuploadPage" && EnableReuploadPagefieldMapping[item?.accessString]) {
                                enableReuploadPageModule[EnableReuploadPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "DistributorConfigurationPage" && DistributorConfigurationPagefieldMapping[item?.accessString]) {
                                distributorConfigurationPageModule[DistributorConfigurationPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "SystemConfigurationPage" && SystemConfigurationPagefieldMapping[item?.accessString]) {
                                systemConfigurationPageModule[SystemConfigurationPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "MastersPage" && MastersPagefieldMapping[item?.accessString.toLowerCase()]) {
                                mastersPagefieldMappingModule[MastersPagefieldMapping[item?.accessString.toLowerCase()]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "ReportPage" && ReportPagefieldMapping[item?.accessString]) {
                                reportPageModule[ReportPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "DashboardPage" && DashboardPagefieldMapping[item?.accessString]) {
                                dashboardPageModule[DashboardPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "DataUploadPage" && DataUploadPagefieldMapping[item?.accessString]) {
                                dataUploadPageModule[DataUploadPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "SNSUploadPage" && SNSUploadPagefieldMapping[item?.accessString]) {
                                snsUploadPageModule[SNSUploadPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "OnlineEntryPage" && OnlineEntryPagefieldMapping[item?.accessString]) {
                                onlineEntryPageModule[OnlineEntryPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "MonthClosurePage" && MonthClosurePagefieldMapping[item?.accessString]) {
                                monthClosurePageModule[MonthClosurePagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "DataPage" && DataPagefieldMapping[item?.accessString]) {
                                dataPageModule[DataPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "HelpTopicPage" && HelpTopicPagefieldMapping[item?.accessString]) {
                                helpTopicPageModule[HelpTopicPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "AddEditHelpTopicsPage" && AddEditHelpTopicsPagefieldMapping[item?.accessString]) {
                                addEditHelpTopicsPageModule[AddEditHelpTopicsPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "AdminMastersPage" && AdminMastersPagefieldMapping[item?.accessString]) {
                                adminMastersPageModule[AdminMastersPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "SubCategoryPage" && SubCategoryPagefieldMapping[item?.accessString]) {
                                subCategoryPageModule[SubCategoryPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                            if (item?.moduleName === "HelpAndCommunicationPage" && HelpAndCommunicationPagefieldMapping[item?.accessString]) {
                                helpAndCommunicationPageModule[HelpAndCommunicationPagefieldMapping[item?.accessString]] = item?.accessStringValue.toString();
                            }
                        });

                        const switchedUserValue = {
                            UserId: response.data?.userId || '0',
                            UserName: response.data?.userFirstName || 'N/A',
                            Role: response.data?.userRole || 'N/A',
                            AssetAccessToken: response.data?.accessToken || 'N/A',
                            HeaderAccessToken: previousToken,
                            RoleId: '',
                            RoleAccess: '',
                            isHnCActive: response.data?.isHnCActive || '',
                            impersonAccess: response.data?.impersonAccess || false,
                            sessionId: response.data?.sessionId || '',
                            DataUploadApprovePageAccess: Object.keys(dataUploadApprovePageModule).length ? [dataUploadApprovePageModule] : [],
                            ReloadZylemDataPageAccess: Object.keys(reloadZylemDataPageModule).length ? [reloadZylemDataPageModule] : [],
                            DataMappingPageAccess: Object.keys(dataMappingPageModule).length ? [dataMappingPageModule] : [],
                            EnableReuploadPageAccess: Object.keys(enableReuploadPageModule).length ? [enableReuploadPageModule] : [],
                            DistributorConfigurationPageAccess: Object.keys(distributorConfigurationPageModule).length ? [distributorConfigurationPageModule] : [],
                            SystemConfigurationPageAccess: Object.keys(systemConfigurationPageModule).length ? [systemConfigurationPageModule] : [],
                            MastersPageAccess: Object.keys(mastersPagefieldMappingModule).length ? [mastersPagefieldMappingModule] : [],
                            ReportPageAccess: Object.keys(reportPageModule).length ? [reportPageModule] : [],
                            DashboardPageAccess: Object.keys(dashboardPageModule).length ? [dashboardPageModule] : [],
                            DataUploadPageAccess: Object.keys(dataUploadPageModule).length ? [dataUploadPageModule] : [],
                            SNSUploadPageAccess: Object.keys(snsUploadPageModule).length ? [snsUploadPageModule] : [],
                            OnlineEntryPageAccess: Object.keys(onlineEntryPageModule).length ? [onlineEntryPageModule] : [],
                            MonthClosurePageAccess: Object.keys(monthClosurePageModule).length ? [monthClosurePageModule] : [],
                            DataPageAccess: Object.keys(dataPageModule).length ? [dataPageModule] : [],
                            HelpTopicPageAccess: Object.keys(helpTopicPageModule).length ? [helpTopicPageModule] : [],
                            AddEditHelpTopicsPageAccess: Object.keys(addEditHelpTopicsPageModule).length ? [addEditHelpTopicsPageModule] : [],
                            AdminMastersPageAccess: Object.keys(adminMastersPageModule).length ? [adminMastersPageModule] : [],
                            SubCategoryPageAccess: Object.keys(subCategoryPageModule).length ? [subCategoryPageModule] : [],
                            HelpAndCommunicationPageAccess: Object.keys(helpAndCommunicationPageModule).length ? [helpAndCommunicationPageModule] : [],
                        };

                        dispatch(switchUser(switchedUserValue));
                        handleCloseSwitchUserPopup();

                        if (role === "j&j coe")
                            navigate(PATH_CoE.root, { state: { data: false } });
                        else if (role === "j&j sales")
                            navigate(PATH_SalesUser.root, { state: { data: false } });
                        else if (role === "dealer")
                            navigate(PATH_DEALER.root);
                        else if (role === "help admin")
                            navigate(PATH_Admin.root, { state: { data: false } });
                        else if (role === "branch secretary")
                            navigate(PATH_BranchSecretaryUser.root, { state: { data: false } });
                        else if (role === "coe viewer")
                            navigate(PATH_CoE.root, { state: { data: false } });
                        else if (role === "coe admin")
                            navigate(PATH_COEAdmin.root, { state: { data: false } });
                        else if (role === "coe data viewer")
                            navigate(PATH_COEAdmin.root, { state: { data: false } });
                        else if (role === "report user")
                            navigate(PATH_ReportUser.root, { state: { data: false } });
                        else if (role === "dashboard & report user")
                            navigate(PATH_DashboardAndReportUser.root, { state: { data: false } });
                        else if (role === "master user")
                            navigate(PATH_MasterUser.root, { state: { data: false } });
                        else if (role === "master viewer")
                            navigate(PATH_MasterViewer.root, { state: { data: false } });

                        setTimeout(() => {
                            window.location.reload();
                        }, 10);
                        setLoading(false);
                    }
                    else {
                        setLoading(false);
                        console.log("error");
                    }
                })
                .catch(_error => {
                    handleError(_error, navigate, "", setLoading);
                });
        }
        else {
            setSwitchUserConfirmation(false);
        }
    };

    const handleSwitchBackToOriginalUser = () => {
        setSwitchBackToUserConfirmation(true);
    }

    const handleSwitchBackToOriginalUserConfirmation = (value, data) => {
        if (value === true) {
            const payload = {
                TargetUserId: primaryUser.UserId,
                SessionId: primaryUser.SessionId
            }
            UserMasterService.SwitchBackToOriginalUser(payload)
                .then((response) => {
                    if (response.status === 200) {
                        const primaryUserData = response.data;
                        const role = primaryUserData.userRole?.toLowerCase();
                        AppCache.RevertToPrimaryUser();
                        dispatch(revertUser());
                        handleCloseSwitchUserPopup();

                        if (role === "j&j coe")
                            navigate(PATH_CoE.root, { state: { data: false } });
                        else if (role === "coe viewer")
                            navigate(PATH_CoE.root, { state: { data: false } });
                        else if (role === "coe admin")
                            navigate(PATH_COEAdmin.root, { state: { data: false } });
                        else if (role === "coe data viewer")
                            navigate(PATH_COEAdmin.root, { state: { data: false } });

                        setTimeout(() => {
                            window.location.reload();
                        }, 10);
                        setLoading(false);
                    }
                    else {
                        setLoading(false);
                        console.log("error");
                    }
                })
                .catch(_error => {
                    handleError(_error, navigate, "", setLoading);
                });
        }
        else {
            setSwitchBackToUserConfirmation(false);
        }
    }

    return (
        <div>
            <Modal open >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: { xs: '10%', sm: '15%', md: '22%' },
                    transform: 'translateY(-50%)',
                    bgcolor: 'background.paper',
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    width: { xs: 400, sm: 500, md: 630, lg: 680 },
                    height: 480,
                    maxHeight: 570
                }}>
                    <Stack direction='row' alignItems="center" justifyContent='space-between' mb={1}>
                        <Stack direction='row' alignItems="center" justifyContent='flex-start' spacing={1}>
                            <Typography variant='h6' className="PageHeader">
                                Switch User Account
                            </Typography>
                            {isImpersonating &&
                                <Box sx={{
                                    padding: '0.22rem 0.4rem',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                    display: 'flex',
                                    backgroundColor: '#f4f4f4',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '6px',
                                    width: { xs: '100px', sm: '200px' }
                                }}>
                                    <Iconify icon="ic:outline-group" className="PrimaryUserStyle" />
                                    <Typography className="PrimaryUserStyle"> Switch to {primaryUserName} </Typography>
                                </Box>
                            }
                        </Stack>
                        <Stack direction='row' alignItems="center" justifyContent='flex-end'>
                            {isImpersonating &&
                                <Button variant="contained" size="small" sx={buttonStyle}
                                    onClick={handleSwitchBackToOriginalUser} >
                                    Reset
                                </Button>
                            }
                            <IconButton onClick={handleCloseSwitchUserPopup} sx={{ p: 0 }}>
                                <Iconify icon="ant-design:close-square-outlined" width='24px' height='24px' />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Box>

                        <Divider sx={{ my: 0.1 }} />
                        <ExtendedCommonDataGrid
                            rowData={rowData}
                            columnData={transformColumnsForTanStack(columnData)}
                            actionColumn={actionColumn}
                            loading={loading}
                            setLoading={setLoading}
                            ClearFilters={ClearFilters}
                            removeFilter={removeFilter}
                            setRemoveFilter={setRemoveFilter}
                            setClickedRowId={setClickedRowId}
                            clickedRowId={clickedRowId}
                            globalFilter={globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            heightP="310px"
                            clear={clear}
                            filteredData={filteredData}
                            setFilteredData={setFilteredData}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            apiGetCall={apiGetCall}
                            setApiGetCall={setApiGetCall}
                        />
                        <Divider sx={{ my: 0.1 }} />

                        <Box sx={{ position: 'absolute', bottom: 10, right: 16 }} >
                            <Button type="submit" variant="contained" size="small" disabled={!selectedUserId}
                                onClick={handleSwitchToUser} >
                                Login
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {switchUserConfirmation && <ConfirmationPopup confirm={handleSwitchToUserConfirmation}
                title='Switch User Account' msg='Are you sure you want to switch user?' />}

            {switchBackToUserConfirmation && <ConfirmationPopup confirm={handleSwitchBackToOriginalUserConfirmation}
                title='Switch Back to Original User' msg={`Are you sure you want to switch back to ${primaryUserName}?`} />}

        </div >
    )
}